import React from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HStep, HTip, HBold } from '../HandbookContent';

export default function ChapterAIAssistant() {
  return (
    <HChapter
      title="AI Assistant"
      subtitle="Use artificial intelligence to generate playbooks, detect gaps, and get workflow guidance."
    >
      <HMetaphor
        icon={<SparklesIcon className="w-5 h-5 text-lex-accent" />}
        title="Your Digital Paralegal"
      >
        Imagine having a paralegal who has studied thousands of legal workflows and can draft a complete
        playbook from a simple description. That's the AI Assistant. Tell it what kind of case you need
        a workflow for, and it drafts the entire structure — statuses, transitions, guards, and
        actions — in seconds. It can also review your existing playbooks and flag missing pieces, like
        a paralegal checking a filing for completeness.
      </HMetaphor>

      <HSection title="Generating a Playbook from a Description">
        <img
          src="/handbook/ai-assistant-panel.svg"
          alt="AI Assistant sidebar panel showing the Generate tab with fields for describing a playbook, practice area (Family Law), jurisdiction (CA-06073), complexity (Standard 5-10 statuses), a Generate Playbook button, and a preview section showing CA Family Law Dissolution with 6 statuses and 8 transitions at 87% confidence"
          className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 w-full"
        />
        <HStep number={1} title="Open the AI Assistant panel">
          In the Visual Editor, click the "AI Assistant" button in the toolbar. A sidebar opens
          on the right with two tabs: Generate and Gap Analysis.
        </HStep>
        <HStep number={2} title="Describe what you need">
          In the "Generate" tab, write a plain-English description of the workflow you want. Be
          specific about the practice area, jurisdiction, and key phases. Example: "Create a
          California family law dissolution playbook with filing, service, discovery, and trial phases."
        </HStep>
        <HStep number={3} title="Set optional parameters">
          Choose the practice area, jurisdiction code, and complexity level (simple: 3-5 statuses,
          standard: 5-10, complex: 10+).
        </HStep>
        <HStep number={4} title="Click 'Generate Playbook'">
          The AI creates a complete playbook structure. You'll see a preview showing the generated
          statuses and transitions, along with a confidence score indicating how well the AI
          understood your request.
        </HStep>
        <HStep number={5} title="Save the result">
          If the preview looks good, click "Save as New Playbook." The generated playbook appears
          in your Library as a draft, ready for review and customization.
        </HStep>
      </HSection>

      <HSection title="Gap Detection">
        <HParagraph>
          The Gap Analysis tab reviews your <HBold>existing playbook</HBold> and identifies potential
          problems:
        </HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-2">
          <li><HBold>Dead-end statuses</HBold> — A status with no outgoing transitions (cases get stuck there forever)</li>
          <li><HBold>Unreachable statuses</HBold> — A status with no incoming transitions (cases can never reach it)</li>
          <li><HBold>Missing guards</HBold> — Transitions that should have role-based or data-based restrictions</li>
          <li><HBold>Missing validators</HBold> — Transitions that should check for required data</li>
          <li><HBold>No notifications</HBold> — Transitions where stakeholders should be notified but aren't</li>
          <li><HBold>No SLA</HBold> — Statuses where cases might linger without a time limit</li>
        </ul>
        <img
          src="/handbook/gap-detection.svg"
          alt="AI Assistant sidebar in Gap Analysis tab showing a score of 72/100 in amber, and three gap cards: Dead-End Status (high severity, rose), Missing Guard (medium severity, amber), and No SLA (low severity, gray), each with descriptions and suggested fixes"
          className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-4 w-full"
        />
        <HParagraph>
          Each gap is rated by severity (high, medium, low) and comes with a suggested fix. You can
          accept the suggestion with one click or dismiss it if it doesn't apply.
        </HParagraph>
      </HSection>

      <HSection title="Ghost Suggestions on the Canvas">
        <HParagraph>
          When the AI detects gaps, it overlays <HBold>ghost elements</HBold> on the Visual Editor
          canvas — semi-transparent purple nodes and arrows showing suggested additions. These don't
          affect your playbook until you explicitly accept them.
        </HParagraph>
      </HSection>

      <HTip>
        The AI Assistant works best when you give it specific descriptions. Instead of "make a
        litigation playbook," try "create a California state court personal injury litigation playbook
        covering complaint filing through trial, with statutory deadline calculations."
      </HTip>
    </HChapter>
  );
}
